<template>
  <ValidationObserver ref='validator' v-slot='{ handleSubmit }' slim>
    <div class='row'>
      <div class='col-xs-12 col-sm-12 col-md-6'>
        <text-box v-model='eanCode' :label="$t('identification.smartMeter.eanCode')"
          :rules="{ 'ean-code-validator': true, required: true }" placeholder='541447856985698523'></text-box>
        <div class='lib__field__note'>
          <a v-if='!isIdentifiedAndCanContinue' href='#' @click='whereFoundEanModalOpen = true'>{{
            $t('ean.whereFoundEan.title') }}</a>
        </div>
      </div>
      <div class="col-xs-12 col-sm-12 col-md-6">
        <text-box :label="$t('prosumerDeclaration.identification.meterLabel')" :max="4" :min="4" onlyNumbers required
          v-model="meterLastFourNumbers"></text-box>
        <div class="lib__field__note">
          <a v-if='!isIdentifiedAndCanContinue' href="#" @click="whereFindMeterLastDigitsOpen = true">
            {{ $t('prosumerDeclaration.verifyMeter.help') }}
          </a>
        </div>
      </div>
    </div>
    <template v-if='!isIdentifiedAndCanContinue'>

      <FindEanModalDialog v-model="whereFoundEanModalOpen" />

      <FindMeterLastDigitsModalDialog v-model="whereFindMeterLastDigitsOpen" />

      <template v-if='!isIdentified'>
        <Recaptcha :site-key='siteKey' :key='recaptchaKey' v-model='recaptchaToken' :by-pass='$testMode' />

        <nav class='lib__btns-nav-list'>
          <button :class="{ 'lib__btn--loading': loading, 'lib__btn--disabled': loading }"
            class='lib__btn lib__btn--rounded lib__btn--primary' type='button' @click='handleSubmit(identify)'>
            {{ $t('identification.smartMeter.identify') }}
          </button>
        </nav>
      </template>

      <template v-if='hasError'>
        <Feedback icon level='danger' outline simple>
          <p v-html='$t(`prosumerDeclaration.identification.eanAndMeterNotFound`)'></p>
        </Feedback>
      </template>

    </template>

  </ValidationObserver>
</template>


<script lang='ts'>
import FindMeterLastDigitsModalDialog from '@/components/display/FindMeterLastDigitsModalDialog.vue'
import FindEanModalDialog from '@/components/display/FindEanModalDialog.vue'
import ZipCodeLocalitySelector from '@/components/inputs/Address/ZipCodeLocalitySelector.vue'
import ConnectionPointArea from '@/components/inputs/Address/ConnectionPointArea.vue'
import { CheckEanForProsumerDeclarationResponse, CheckEanSearch } from '@/models'
import CollapseInfo from '@/components/display/CollapseInfo.vue'
import { GridAccessPoint } from '@/models/prosumer-declaration'
import { Emit, Vue, Watch } from 'vue-property-decorator'
import { helper, chatService } from '@/services'
import { Mutation, State } from 'vuex-class'
import Component from 'vue-class-component'
import { AxiosResponse } from 'axios'
import { CacheAxiosResponse } from 'axios-cache-interceptor'

@Component({
  name: 'identification',
  components: {
    ZipCodeLocalitySelector,
    ConnectionPointArea,
    CollapseInfo,
    FindEanModalDialog,
    FindMeterLastDigitsModalDialog
  }
})
export default class Identification extends Vue {
  //#region [Data]
  @State('eanCode', { namespace: 'prosumerDeclaration/identification' })
  public eanCodeState!: string | null
  @State('meterLastFourNumbers', { namespace: 'prosumerDeclaration/identification' })
  public meterLastFourNumbersState!: string | null
  @State('requestId', { namespace: 'prosumerDeclaration' })
  public requestId!: string
  @State('gridAccessPoint', { namespace: 'prosumerDeclaration/identification' })
  public gridAccessPointState!: GridAccessPoint
  @State('lang')
  public lang!: string | null

  public eanCode: string | null = null
  public recaptchaToken: string | null = null
  public gridAccessPoint: GridAccessPoint | null = null
  public loading: boolean = false
  public hasError: boolean = false
  public isIdentified: boolean = false
  public recaptchaKey: number = 0
  public meterLastFourNumbers: string | null = null
  public whereFoundEanModalOpen: boolean = false
  public whereFindMeterLastDigitsOpen: boolean = false
  //#endregion

  //#region [Mutation]
  @Mutation('STORE_GRID_ACCESS_POINT', { namespace: 'prosumerDeclaration/identification' })
  public storeGridAccessPoint!: (index: GridAccessPoint | null) => void
  @Mutation('CLEAR_API_ERRORS', { namespace: 'apiErrors' })
  private clearApiErrors!: () => void
  //#endregion

  //#region [Computed]
  public get isIdentifiedAndCanContinue(): boolean {
    return this.isIdentified && !!this.gridAccessPoint
  }
  public get siteKey() {
    return process.env.VUE_APP_CAPTCHA_SITE_KEY
  }
  //#endregion

  //#region [Watch]
  @Watch('eanCode')
  @Emit('eanCode')
  public onEanCodeChange(value: string | null) {
    this.clearApiErrors()
    return value
  }

  @Watch('meterLastFourNumbers')
  @Emit('meterLastFourNumbers')
  public onMeterLastFourNumbers(meterLastFourNumbers: string | null) {
    return meterLastFourNumbers
  }

  @Watch('eanCodeState')
  public onEanCodeStateChange(value: string | null) {
    if (!value) {
      this.isIdentified = false
    }
  }

  @Watch('isIdentified')
  @Emit('isIdentified')
  public onIsIdentifiedChange(value: boolean): boolean | null {
    return value
  }

  @Watch('isIdentifiedAndCanContinue', { immediate: true })
  @Emit('canContinue')
  public isIdentifiedAndCanContinueChange(value: boolean): boolean | null {
    return value
  }

  @Watch('gridAccessPoint')
  @Emit('gridAccessPoint')
  public onGridAccessPointChange(value: GridAccessPoint):
    GridAccessPoint | null {
    return value
  }
  //#endregion

  //#region [Method]
  public mounted() {
    this.eanCode = helper.clone(this.eanCodeState)
    this.meterLastFourNumbers = helper.clone(this.meterLastFourNumbersState)
    this.gridAccessPoint = helper.clone(this.gridAccessPointState)
    this.isIdentified = !!this.gridAccessPointState
  }

  public async checkEanForProsumerDeclaration(): Promise<CheckEanForProsumerDeclarationResponse> {

    this.hasError = false;

    if (!this.recaptchaToken) {
      throw new Error('No recaptcha token found');
    }

    const response = await this.$api.get<CheckEanForProsumerDeclarationResponse>(
      `api/assets/check-ean-meter/prosumer-declaration?ean=${this.eanCode!.trim()}&meterLastFourNumbers=${this.meterLastFourNumbers}`,
      {
        headers: {
          'g-recaptcha-response': this.recaptchaToken
        },
        cache: {
          cachePredicate: (status) => true,
        }
      }
    )
    if (response.status !== 200) {
      this.hasError = true;
      throw new Error("Grid access point not found");
    }
    return response.data
  }

  public async identify() {
    if (this.eanCode) {
      try {
        this.loading = true

        const checkEanForProsumerDeclarationResponse = await this.checkEanForProsumerDeclaration()

        this.gridAccessPoint = checkEanForProsumerDeclarationResponse.gridAccessPoint
        this.storeGridAccessPoint(this.gridAccessPoint)

        if (this.gridAccessPoint && checkEanForProsumerDeclarationResponse.token) {
          sessionStorage.setItem('token', checkEanForProsumerDeclarationResponse.token)
          this.isIdentified = true
        }

        this.loading = false

        if (!this.isIdentifiedAndCanContinue) {
          // Refresh the captcha for next identification to work
          this.recaptchaKey += 1
        }
      } catch (err) {
        this.loading = false
        this.isIdentified = false
        this.storeGridAccessPoint(null)
        this.recaptchaKey += 1
      }
    }
  }

  public openChat() {
    chatService.openChat()
  }

  //#endregion

  //#region [Emit]
  //#endregion
}
</script>
